/**
 * The Investor Portals view's rules (#11635 Round 4, documents-ui spec R17 to
 * R20; #15170): which services a client's pages list, the coverage rows of
 * the overview's Portals tab, and how the portal documents group, by manager
 * on the overview and by day on a portal's page. Pure functions over the
 * services read and the search rows, so every rule is unit-tested against a
 * fixed clock and the components only draw.
 */
import type { PortalService } from "@/components/FpFundBrain/Manage/Connections/InvestorPortalsRow";
import { formatRelativeTemporal } from "@/components/FpFundBrain/Manage/Connections/relativeTime";
import type { Temporal } from "@/utils/temporal";
import {
  formatInstant,
  previousCalendarDay,
  todayInZone,
} from "@/utils/temporal";
import type { DocumentRow, LinkedEntity } from "./api";
import { statusBucketOf } from "./rows";

/** The uploader key a service's documents carry, from the service's name
 *  (spec R11 / R22; Round 7, R22: a service's page is scoped by it). */
export const serviceKeyOf = (name: string): string => `system:${name}`;

/** The uploader key a service's documents carry (spec R11 / R22). */
export const serviceUploaderKey = (service: PortalService): string =>
  serviceKeyOf(service.service);

/** The column's order for the portals under the Investor Portals row: by
 *  name (#15170). A client's page says nothing about a portal's state, and
 *  an order by state would say it. */
export const sortServicesForColumn = (
  services: PortalService[]
): PortalService[] =>
  [...services].sort((a, b) =>
    a.service.localeCompare(b.service, undefined, { sensitivity: "base" })
  );

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

/** A wire temporal as "2h ago" (instant) or the day itself (date); a dash
 *  for none. The overview's Last sync cell and the status texts share it. */
export const relativeOf = (value: Temporal | null): string =>
  value === null ? "—" : formatRelativeTemporal(value);

/** A wire temporal as its month and day in the viewer's zone; a dash for
 *  none. The overview's Last document cell and the status texts share it. */
export const monthDayOf = (value: Temporal | null, zone: string): string => {
  if (value === null) return "—";
  switch (value.granularity) {
    case "instant":
      return formatInstant(value.at, zone, "monthDay");
    case "date":
      return value.day;
    default: {
      const unhandled: never = value;
      throw new Error(
        `unhandled temporal granularity: ${JSON.stringify(unhandled)}`
      );
    }
  }
};

// --- the documents half --------------------------------------------------

/** What a portal document groups under (spec R20): the first manager it is
 *  linked to; failing that, the folder it sits in; a row still processing
 *  with neither sits under Processing. */
export type RowGroup =
  | { kind: "record"; key: string; label: string; record: LinkedEntity }
  | { kind: "folder"; key: string; label: string }
  | { kind: "processing"; key: "processing"; label: "Processing" }
  | { kind: "day"; key: `day:${DayBucket}`; label: DayLabel };

/** A portal page's day groups (#15170), newest first. */
export type DayBucket = "today" | "yesterday" | "earlier";
type DayLabel = "Today" | "Yesterday" | "Earlier";

const DAY_LABEL: Record<DayBucket, DayLabel> = {
  today: "Today",
  yesterday: "Yesterday",
  earlier: "Earlier",
};

/** The day a portal page groups a document under (#15170): its upload's
 *  calendar day in the viewer's zone against today's there. A row with no
 *  upload instant sits under Earlier. */
export const dayGroupOf = (
  row: DocumentRow,
  now: Date,
  zone: string
): RowGroup => {
  const today = todayInZone(zone, now);
  const day = row.uploadedAt ? formatInstant(row.uploadedAt, zone) : null;
  const bucket: DayBucket =
    day === today
      ? "today"
      : day !== null && day === previousCalendarDay(today)
        ? "yesterday"
        : "earlier";
  return { kind: "day", key: `day:${bucket}`, label: DAY_LABEL[bucket] };
};

export const groupOf = (row: DocumentRow): RowGroup => {
  const manager = row.linkedEntities.find((e) => e.kind === "manager");
  if (manager)
    return {
      kind: "record",
      key: `record:${manager.publicId}`,
      label: manager.name,
      record: manager,
    };
  if (row.filePath.length > 0) {
    const folder = row.filePath[row.filePath.length - 1] ?? "";
    return {
      kind: "folder",
      key: `folder:${JSON.stringify([row.driveId, row.filePath])}`,
      label: folder,
    };
  }
  if (statusBucketOf(row) === "processing")
    return { kind: "processing", key: "processing", label: "Processing" };
  return { kind: "folder", key: "folder:root", label: "Documents" };
};

/** The rows reordered so each group's rows sit together (spec R20): groups
 *  in the order their newest row appears, Processing first; rows keep their
 *  own order within a group. The stream is newest-first already, so first
 *  appearance is newest document. */
export const groupRows = (
  rows: DocumentRow[],
  of: (row: DocumentRow) => RowGroup = groupOf
): DocumentRow[] => {
  const order: string[] = [];
  const byGroup = new Map<string, DocumentRow[]>();
  for (const row of rows) {
    const group = of(row);
    const bucket = byGroup.get(group.key);
    if (bucket) bucket.push(row);
    else {
      byGroup.set(group.key, [row]);
      order.push(group.key);
    }
  }
  const processingFirst = [
    ...order.filter((key) => key === "processing"),
    ...order.filter((key) => key !== "processing"),
  ];
  return processingFirst.flatMap((key) => byGroup.get(key) ?? []);
};

// --- a service's page (Round 8, R26) ---------------------------------------

/** One card in a service's Managers section (Round 9, R28). */
export interface ManagerCard {
  name: string;
  /** The manager's record, to open; null for a name with none. */
  publicId: string | null;
  /** The record's kind, for opening it. */
  kind: string;
  /** Documents that arrived through the service linked to the manager. */
  documentCount: number;
}

const sameName = (a: string, b: string): boolean =>
  a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();

/** A service's managers (R28): the managers its documents are linked to, in
 *  the read's order (most documents first), then the record linked at setup
 *  when it is a manager and not already among them (a record of another
 *  kind, a vehicle, is not a manager card). The typed `managers` list is
 *  never a card: it holds whatever was typed at setup, and nothing ties it
 *  to a record. A row from before the kind was stored reads as a manager. */
export const serviceManagers = (service: PortalService): ManagerCard[] => {
  const cards: ManagerCard[] = service.delivered_managers.map((manager) => ({
    name: manager.name,
    publicId: manager.public_id,
    kind: "manager",
    documentCount: manager.document_count,
  }));
  const linkedName = service.entity_name?.trim() ?? "";
  const linkedId = service.entity_id;
  if (
    linkedId !== null &&
    linkedName !== "" &&
    (service.entity_kind ?? "manager") === "manager" &&
    !cards.some(
      (card) => card.publicId === linkedId || sameName(card.name, linkedName)
    )
  )
    cards.push({
      name: linkedName,
      publicId: linkedId,
      kind: "manager",
      documentCount: 0,
    });
  return cards;
};

// --- #15170: the client's Investor Portals page ----------------------------

/** The services a client's pages list (#15170): the client-healthy ones.
 *  A service that is not is simply absent; nothing says why. */
export const clientServices = (services: PortalService[]): PortalService[] =>
  services.filter((service) => service.client_healthy);

/** One manager and portal pair on the Portals tab (#15170). */
export interface CoverageRow {
  key: string;
  service: PortalService;
  /** The manager the portal delivers for; null for a portal matched to none
   *  yet. */
  manager: { name: string; publicId: string } | null;
  documentCount: number;
  lastDocumentAt: Temporal | null;
}

/** The Portals tab's rows (#15170): one per portal and delivered manager,
 *  in the read's order; a portal with no delivered manager is one row with
 *  the manager blank, counting what the portal holds. */
export const coverageRows = (services: PortalService[]): CoverageRow[] =>
  services.flatMap((service): CoverageRow[] =>
    service.delivered_managers.length > 0
      ? service.delivered_managers.map((manager) => ({
          key: `${service.connection_id}:${manager.public_id}`,
          service,
          manager: { name: manager.name, publicId: manager.public_id },
          documentCount: manager.document_count,
          lastDocumentAt: manager.newest_document_at,
        }))
      : [
          {
            key: `${service.connection_id}:none`,
            service,
            manager: null,
            documentCount: service.coverage?.held ?? 0,
            lastDocumentAt: service.last_document_at,
          },
        ]
  );

/** How the Portals tab groups its rows (#15170). */
export type CoverageGrouping = "manager" | "portal";

export interface CoverageGroup {
  key: string;
  label: string;
  /** The portal, when the group is one: its heading carries the logo. */
  service: PortalService | null;
  rows: CoverageRow[];
}

/** The label of the group holding the portals matched to no manager. */
export const UNMATCHED_LABEL = "Not yet matched to a manager";

const byName = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

/** The rows grouped (#15170): by manager A to Z, portals A to Z inside, the
 *  unmatched portals last; or by portal A to Z, managers A to Z inside with
 *  a blank manager last. */
export const coverageGroups = (
  rows: CoverageRow[],
  grouping: CoverageGrouping
): CoverageGroup[] => {
  const groups = new Map<string, CoverageGroup>();
  for (const row of rows) {
    const group: Omit<CoverageGroup, "rows"> =
      grouping === "manager"
        ? row.manager === null
          ? { key: "unmatched", label: UNMATCHED_LABEL, service: null }
          : {
              key: `manager:${row.manager.publicId}`,
              label: row.manager.name,
              service: null,
            }
        : {
            key: `portal:${row.service.connection_id}`,
            label: row.service.service,
            service: row.service,
          };
    const existing = groups.get(group.key);
    if (existing) existing.rows.push(row);
    else groups.set(group.key, { ...group, rows: [row] });
  }
  const inner = (a: CoverageRow, b: CoverageRow): number =>
    grouping === "manager"
      ? byName(a.service.service, b.service.service)
      : a.manager === null
        ? 1
        : b.manager === null
          ? -1
          : byName(a.manager.name, b.manager.name);
  return [...groups.values()]
    .map((group) => ({ ...group, rows: [...group.rows].sort(inner) }))
    .sort((a, b) =>
      a.key === "unmatched"
        ? 1
        : b.key === "unmatched"
          ? -1
          : byName(a.label, b.label)
    );
};

/** The Portals tab's heading (#15170), counted from the rows shown: the
 *  distinct managers (a blank manager counts as none) and portals. */
export const coverageHeading = (rows: CoverageRow[]): string => {
  const managers = new Set(
    rows.flatMap((row) => (row.manager === null ? [] : [row.manager.publicId]))
  );
  const portals = new Set(rows.map((row) => row.service.connection_id));
  return `Covering ${plural(managers.size, "manager")} across ${plural(
    portals.size,
    "portal"
  )}`;
};
